import { useMemo, useState } from 'react';

import { Field, Modal, Notice } from '@/components/Modal';
import { EmptyCard, LoadingBlock, PageHeader } from '@/components/PageHeader';
import { PersonnelSearchInput } from '@/components/PersonnelSearchInput';
import { errorMessage, inputStyle } from '@/lib/format';
import { normalizeSearch } from '@/lib/text/normalizeSearch';

import {
  STATUS_LABEL,
  useAccountOptions,
  useAccountPassword,
  useAccounts,
  useAccountStatus,
  useSaveAccount,
  type Account,
  type AccountInput,
  type AccountKind,
  type Option,
} from './accountApi';

/**
 * 계정 관리(DOC-141 §3 · 사용자 지시 2026-11-02 「협력업체 지정 · 등록」) — 이 서비스에 들어오는 사람.
 * 협력업체는 업체 명부에서 업체를 고르고 사람을 적고, 직원은 인사 명부에서 고른다. 붙인 현장의 화면 · 일지를 쓴다.
 * 지우지 않는다 — 끄기 · 다시 켜기만. 권한이 없으면 서버가 답한다(버튼을 감추지 않는다).
 */
export function AccountsPage() {
  const accounts = useAccounts();
  const status = useAccountStatus();
  const [kind, setKind] = useState<'' | AccountKind>('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<{ account: Account | null; kind: AccountKind } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => {
    const needle = normalizeSearch(query);
    return (accounts.data ?? []).filter(
      (a) =>
        (!kind || a.kind === kind) &&
        (!needle || normalizeSearch([a.name, a.email, a.phone, a.vendorName, ...a.sites.map((s) => s.name)].join(' ')).includes(needle)),
    );
  }, [accounts.data, kind, query]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="계정 관리"
        actions={
          <div className="flex items-center gap-2">
            <button className="btn-secondary" onClick={() => setEditing({ account: null, kind: 'STAFF' })}>
              직원 등록
            </button>
            <button className="btn-primary" onClick={() => setEditing({ account: null, kind: 'VENDOR' })}>
              협력업체 등록
            </button>
          </div>
        }
      />

      <div className="card p-4" data-tour="page-filters">
        <div className="flex items-center gap-2 flex-wrap">
          {(
            [
              ['', '전체'],
              ['VENDOR', '협력업체'],
              ['STAFF', '직원'],
            ] as const
          ).map(([value, label]) => (
            <button key={value} type="button" className={kind === value ? 'btn-primary' : 'btn-secondary'} onClick={() => setKind(value)}>
              {label}
            </button>
          ))}
          <input
            className="input"
            style={{ ...inputStyle, width: '260px' }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="이름 · 이메일 · 업체 · 현장"
            aria-label="찾기"
          />
        </div>
        {error && (
          <div style={{ marginTop: '10px' }}>
            <Notice tone="warning" onClose={() => setError(null)}>
              {error}
            </Notice>
          </div>
        )}
      </div>

      {accounts.isPending ? (
        <LoadingBlock />
      ) : rows.length === 0 ? (
        <EmptyCard>등록된 계정이 없습니다.</EmptyCard>
      ) : (
        <div className="card p-0" style={{ overflowX: 'auto' }} data-tour="page-list">
          <table className="site-table" style={{ fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ width: '80px' }}>구분</th>
                <th style={{ textAlign: 'left' }}>업체</th>
                <th>이름</th>
                <th style={{ textAlign: 'left' }}>이메일</th>
                <th>휴대폰</th>
                <th style={{ textAlign: 'left' }}>현장</th>
                <th>역할</th>
                <th style={{ width: '100px' }}>상태</th>
                <th style={{ width: '90px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const st = STATUS_LABEL[a.status];
                return (
                  <tr key={a.id} style={{ cursor: 'pointer', opacity: a.status === 'DISABLED' ? 0.55 : 1 }} onClick={() => setEditing({ account: a, kind: a.kind })}>
                    <td>{a.kind === 'VENDOR' ? '협력업체' : '직원'}</td>
                    <td style={{ textAlign: 'left' }}>{a.vendorName ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{a.name}</td>
                    <td style={{ textAlign: 'left' }}>{a.email}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{a.phone ?? '—'}</td>
                    <td style={{ textAlign: 'left', maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.sites.length ? a.sites.map((s) => s.name).join(', ') : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{a.roles.length ? a.roles.map((r) => r.name).join(', ') : '—'}</td>
                    <td>
                      <span className={`badge ${st.tone}`}>{st.label}</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={status.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          setError(null);
                          status.mutate({ id: a.id, active: a.status === 'DISABLED' }, { onError: (cause) => setError(errorMessage(cause)) });
                        }}
                      >
                        {a.status === 'DISABLED' ? '켜기' : '끄기'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <AccountModal account={editing.account} kind={editing.kind} onClose={() => setEditing(null)} />}
    </div>
  );
}

function AccountModal({ account, kind, onClose }: { account: Account | null; kind: AccountKind; onClose: () => void }) {
  const options = useAccountOptions();
  const save = useSaveAccount();
  const password = useAccountPassword();
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [input, setInput] = useState<AccountInput>(() => ({
    kind,
    vendorId: account?.vendorId ?? null,
    personnelId: account?.personnelId ?? null,
    name: account?.name ?? '',
    email: account?.email ?? '',
    phone: account?.phone ?? '',
    password: '',
    siteIds: account?.sites.map((s) => s.id) ?? [],
    roleIds: account?.roles.map((r) => r.id) ?? [],
  }));
  const set = (patch: Partial<AccountInput>) => setInput((prev) => ({ ...prev, ...patch }));
  const opts = options.data;
  const vendor = kind === 'VENDOR';

  const submit = () => {
    setError(null);
    save.mutate({ id: account?.id ?? null, input }, { onSuccess: onClose, onError: (cause) => setError(errorMessage(cause)) });
  };
  const submitPassword = () => {
    if (!account) return;
    setError(null);
    password.mutate(
      { id: account.id, password: newPassword },
      { onSuccess: () => setNewPassword(''), onError: (cause) => setError(errorMessage(cause)) },
    );
  };

  return (
    <Modal
      title={`${vendor ? '협력업체' : '직원'} 계정 ${account ? '수정' : '등록'}`}
      maxWidth="720px"
      onClose={onClose}
      actions={
        <button type="button" className="btn-primary" disabled={save.isPending} onClick={submit}>
          {save.isPending ? '저장 중…' : '저장'}
        </button>
      }
    >
      <div className="space-y-4">
        {error && <Notice tone="error">{error}</Notice>}
        {options.isPending ? (
          <LoadingBlock />
        ) : (
          <>
            {vendor ? (
              <Field label="협력업체">
                <OptionSearch options={opts?.vendors ?? []} value={input.vendorId} onChange={(vendorId) => set({ vendorId })} placeholder="업체 이름" />
              </Field>
            ) : account ? (
              <Field label="직원">
                <div>{account.name}</div>
              </Field>
            ) : (
              <Field label="직원">
                <PersonnelSearchInput
                  people={(opts?.personnel ?? []).map((p) => ({ id: p.id, name: p.name }))}
                  onPick={(personnelId) => set({ personnelId, name: opts?.personnel.find((p) => p.id === personnelId)?.name ?? input.name })}
                />
                {input.personnelId && <div style={{ marginTop: '6px', fontSize: '13px' }}>고른 직원: {input.name}</div>}
              </Field>
            )}
            <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
              <Field label="이름">
                <input className="input" style={inputStyle} value={input.name} onChange={(e) => set({ name: e.target.value })} />
              </Field>
              <Field label="이메일(로그인)">
                <input className="input" style={inputStyle} type="email" value={input.email} onChange={(e) => set({ email: e.target.value })} />
              </Field>
              <Field label="휴대폰">
                <input className="input" style={inputStyle} value={input.phone} onChange={(e) => set({ phone: e.target.value })} />
              </Field>
            </div>
            {account ? (
              <Field label="비밀번호">
                <div className="flex items-center gap-2">
                  <input
                    className="input"
                    style={{ ...inputStyle, width: '240px' }}
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="8자 이상"
                  />
                  <button type="button" className="btn-secondary" disabled={password.isPending} onClick={submitPassword}>
                    비밀번호 바꾸기
                  </button>
                  {password.isSuccess && <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>바꿨습니다.</span>}
                </div>
              </Field>
            ) : (
              <Field label="첫 비밀번호">
                <input
                  className="input"
                  style={{ ...inputStyle, width: '240px' }}
                  type="password"
                  autoComplete="new-password"
                  value={input.password}
                  onChange={(e) => set({ password: e.target.value })}
                  placeholder="8자 이상"
                />
              </Field>
            )}
            <Field label={`현장 (${input.siteIds.length})`}>
              <MultiPick options={opts?.sites ?? []} value={input.siteIds} onChange={(siteIds) => set({ siteIds })} placeholder="현장 이름" />
            </Field>
            <Field label="역할">
              {(opts?.roles ?? []).length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>—</div>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {(opts?.roles ?? []).map((r) => (
                    <label key={r.id} className="flex items-center gap-1.5" style={{ fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={input.roleIds.includes(r.id)}
                        onChange={(e) => set({ roleIds: e.target.checked ? [...input.roleIds, r.id] : input.roleIds.filter((id) => id !== r.id) })}
                      />
                      {r.name}
                    </label>
                  ))}
                </div>
              )}
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

/** 하나 고르기 — 적어서 찾는다(업체가 수백 곳이다). */
function OptionSearch({ options, value, onChange, placeholder }: { options: Option[]; value: string | null; onChange: (id: string | null) => void; placeholder: string }) {
  const [query, setQuery] = useState('');
  const picked = options.find((o) => o.id === value) ?? null;
  const needle = normalizeSearch(query);
  const matches = needle ? options.filter((o) => normalizeSearch(`${o.name} ${o.note ?? ''}`).includes(needle)).slice(0, 8) : [];
  return (
    <div>
      {picked && (
        <div className="flex items-center gap-2" style={{ marginBottom: '6px', fontSize: '13px' }}>
          <span className="badge badge-blue">{picked.name}</span>
          {picked.note && <span style={{ color: 'var(--text-muted)' }}>{picked.note}</span>}
        </div>
      )}
      <input className="input" style={inputStyle} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} />
      {matches.length > 0 && (
        <div className="card p-0" style={{ marginTop: '4px' }}>
          {matches.map((o) => (
            <button
              key={o.id}
              type="button"
              className="block w-full text-left px-3 py-2"
              style={{ fontSize: '13px' }}
              onClick={() => {
                onChange(o.id);
                setQuery('');
              }}
            >
              {o.name}
              {o.note && <span style={{ marginLeft: '6px', color: 'var(--text-muted)' }}>{o.note}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** 여럿 고르기 — 고른 것은 알약, 적어서 찾아 더한다. */
function MultiPick({ options, value, onChange, placeholder }: { options: Option[]; value: string[]; onChange: (ids: string[]) => void; placeholder: string }) {
  const [query, setQuery] = useState('');
  const needle = normalizeSearch(query);
  const matches = needle ? options.filter((o) => !value.includes(o.id) && normalizeSearch(o.name).includes(needle)).slice(0, 8) : [];
  const name = (id: string) => options.find((o) => o.id === id)?.name ?? id;
  return (
    <div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" style={{ marginBottom: '6px' }}>
          {value.map((id) => (
            <button key={id} type="button" className="badge badge-blue" onClick={() => onChange(value.filter((v) => v !== id))}>
              {name(id)} ✕
            </button>
          ))}
        </div>
      )}
      <input className="input" style={inputStyle} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} />
      {matches.length > 0 && (
        <div className="card p-0" style={{ marginTop: '4px' }}>
          {matches.map((o) => (
            <button
              key={o.id}
              type="button"
              className="block w-full text-left px-3 py-2"
              style={{ fontSize: '13px' }}
              onClick={() => {
                onChange([...value, o.id]);
                setQuery('');
              }}
            >
              {o.name}
              {o.note && <span style={{ marginLeft: '6px', color: 'var(--text-muted)' }}>{o.note}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
